import type { Locale } from '../i18n';
import { PUBLIC_HOLIDAYS, SCHOOL_HOLIDAYS } from './data';

/**
 * School holidays and public holidays of the family's federal state (Kalender 1, 1.24). They come
 * with the app (no request, works offline) and pause the timetables by themselves – before, a
 * family had to enter "Ferien" as an event.
 */

export const REGIONS = [
  'BW',
  'BY',
  'BE',
  'BB',
  'HB',
  'HH',
  'HE',
  'MV',
  'NI',
  'NW',
  'RP',
  'SL',
  'SN',
  'ST',
  'SH',
  'TH',
] as const;
export type Region = (typeof REGIONS)[number];

export type VacationKey =
  | 'autumn'
  | 'easter'
  | 'christmas'
  | 'whitsun'
  | 'ascension'
  | 'summer'
  | 'spring'
  | 'winter'
  | 'dayOff';

const REGION_NAMES: Record<Region, { de: string; en: string }> = {
  BW: { de: 'Baden-Württemberg', en: 'Baden-Württemberg' },
  BY: { de: 'Bayern', en: 'Bavaria' },
  BE: { de: 'Berlin', en: 'Berlin' },
  BB: { de: 'Brandenburg', en: 'Brandenburg' },
  HB: { de: 'Bremen', en: 'Bremen' },
  HH: { de: 'Hamburg', en: 'Hamburg' },
  HE: { de: 'Hessen', en: 'Hesse' },
  MV: { de: 'Mecklenburg-Vorpommern', en: 'Mecklenburg-Western Pomerania' },
  NI: { de: 'Niedersachsen', en: 'Lower Saxony' },
  NW: { de: 'Nordrhein-Westfalen', en: 'North Rhine-Westphalia' },
  RP: { de: 'Rheinland-Pfalz', en: 'Rhineland-Palatinate' },
  SL: { de: 'Saarland', en: 'Saarland' },
  SN: { de: 'Sachsen', en: 'Saxony' },
  ST: { de: 'Sachsen-Anhalt', en: 'Saxony-Anhalt' },
  SH: { de: 'Schleswig-Holstein', en: 'Schleswig-Holstein' },
  TH: { de: 'Thüringen', en: 'Thuringia' },
};

export const regionName = (r: Region, locale: Locale) => REGION_NAMES[r][locale];

/**
 * The place search answers with the state's name (German or English, as asked) –
 * "Nordrhein-Westfalen" or "North Rhine-Westphalia" → "NW". Other countries: undefined.
 */
export function regionFromAdmin(
  admin1: string | undefined,
  countryCode?: string,
): Region | undefined {
  if (!admin1 || (countryCode && countryCode.toUpperCase() !== 'DE')) return undefined;
  const a = admin1.trim().toLowerCase();
  return REGIONS.find(
    (r) => REGION_NAMES[r].de.toLowerCase() === a || REGION_NAMES[r].en.toLowerCase() === a,
  );
}

const VACATION_NAMES: Record<VacationKey, { de: string; en: string }> = {
  autumn: { de: 'Herbstferien', en: 'Autumn holidays' },
  easter: { de: 'Osterferien', en: 'Easter holidays' },
  christmas: { de: 'Weihnachtsferien', en: 'Christmas holidays' },
  whitsun: { de: 'Pfingstferien', en: 'Whitsun holidays' },
  ascension: { de: 'Himmelfahrtsferien', en: 'Ascension holidays' },
  summer: { de: 'Sommerferien', en: 'Summer holidays' },
  spring: { de: 'Frühjahrsferien', en: 'Spring holidays' },
  winter: { de: 'Winterferien', en: 'Winter holidays' },
  dayOff: { de: 'Schulfrei', en: 'No school' },
};

const HOLIDAY_EN: Record<string, string> = {
  Neujahrstag: "New Year's Day",
  'Heilige Drei Könige': 'Epiphany',
  Frauentag: "International Women's Day",
  Karfreitag: 'Good Friday',
  Ostersonntag: 'Easter Sunday',
  Ostermontag: 'Easter Monday',
  'Tag der Arbeit': 'Labour Day',
  'Christi Himmelfahrt': 'Ascension Day',
  Pfingstsonntag: 'Whit Sunday',
  Pfingstmontag: 'Whit Monday',
  Fronleichnam: 'Corpus Christi',
  'Mariä Himmelfahrt': 'Assumption Day',
  Weltkindertag: "World Children's Day",
  'Tag der Deutschen Einheit': 'German Unity Day',
  Reformationstag: 'Reformation Day',
  Allerheiligen: "All Saints' Day",
  'Buß- und Bettag': 'Day of Repentance and Prayer',
  '1. Weihnachtstag': 'Christmas Day',
  '2. Weihnachtstag': 'Boxing Day',
};

export interface SchoolHoliday {
  start: string;
  end: string;
  key: VacationKey;
  name: string;
}

/** The school holiday a day falls into, if any. */
export function schoolHolidayOn(
  region: Region,
  date: string,
  locale: Locale,
): SchoolHoliday | undefined {
  const hit = SCHOOL_HOLIDAYS[region].find(([a, b]) => a <= date && b >= date);
  return hit && { start: hit[0], end: hit[1], key: hit[2], name: VACATION_NAMES[hit[2]][locale] };
}

/** The public holiday's name on that day, if any ("Allerheiligen" / "All Saints' Day"). */
export function publicHolidayOn(region: Region, date: string, locale: Locale): string | undefined {
  const name = PUBLIC_HOLIDAYS[region].find(([d]) => d === date)?.[1];
  if (!name) return undefined;
  return locale === 'en' ? (HOLIDAY_EN[name] ?? name) : name;
}

/** No school that day: holidays, public holidays and weekends. */
export const isSchoolFree = (region: Region, date: string) =>
  SCHOOL_HOLIDAYS[region].some(([a, b]) => a <= date && b >= date) ||
  PUBLIC_HOLIDAYS[region].some(([d]) => d === date);

/** School holidays overlapping from..to, for "Bald" lists and bands. */
export function schoolHolidaysBetween(
  region: Region,
  from: string,
  to: string,
  locale: Locale,
): SchoolHoliday[] {
  return SCHOOL_HOLIDAYS[region]
    .filter(([a, b]) => b >= from && a <= to)
    .map(([start, end, key]) => ({ start, end, key, name: VACATION_NAMES[key][locale] }));
}

export { HOLIDAY_YEARS } from './data';
